/**
 * RegistrationForm — the Registration step's three field sections
 * (General Information · Sample Information · Report Requirements), laid out
 * exactly as the approved prototype and the legacy XAF view
 * `Samplecheckin_DetailView_Copy_SampleRegistration`.
 *
 * Field order and behaviours follow the user manual's Entry Guidance table
 * (surfaced here as a hover tooltip on every label):
 *   · LEFT column  = Job ID → … → Date Expect
 *   · RIGHT column = Relinquished By → … → Status
 *   · Client and Sample Matrix are the only REQUIRED fields.
 *   · Sample Matrix drives the flow: Test and No Of Samples stay GATED until a
 *     matrix is chosen ("without a sample matrix, how can you select a test?").
 *   · Client / Project selections auto-fill their read-only dependants.
 *   · TAT sets Due Date (Received Date + TAT).
 *
 * Presentational only — all state lives in SampleRegistrationDetailPage.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { LookupCombo } from './LookupCombo';
import { UsDateInput } from '../../../components/UsDateInput';
import { UsDateTimeInput } from '../../../components/UsDateTimeInput';
import {
  GUIDANCE, NEWABLE, type SrHeader, type SrLookup, type SrLookupKey,
} from './srData';

export interface RegistrationFormProps {
  header: SrHeader;
  /** Patch one or more header fields. */
  patch: (p: Partial<SrHeader>) => void;
  /** The live lookup catalogs (quick-adds prepend to these). */
  lookups: Record<SrLookupKey, SrLookup>;
  /** Open the quick-add dialog for a lookup. */
  onNewLookup: (kind: SrLookupKey) => void;
  /** Open the Import window (Quote → Import Samples · Project → Import Pre-Log). */
  onImport: (kind: 'quote' | 'coc') => void;
  /** Shared warning (routes to the blocking modal). */
  onWarn: (message: string) => void;
  /** Field ids to flash after a "required fields missing" dialog. */
  flash: ReadonlySet<string>;
  /** True once the user has attempted a save — turns on the required tint. */
  triedSave: boolean;
  readOnly?: boolean;
  /** True once the job has been saved (JobID assigned). Locks No Of Samples and
   *  Test entirely; Matrix allows additions but blocks removal. */
  savedJob?: boolean;
  /** StatusID → display name map (from rule-data API, result set 3). */
  statusNameMap?: Record<string, string>;
  /** Total tests already assigned across every sample — gates "Import Pre-Log"
   *  exactly like legacy's button criteria "[SampleTestCount] = 0". */
  testCount?: number;
  /** Samples currently on the job. A saved job with NO samples (e.g. after Reset
   *  Job ID) is empty again, so Import Pre-Log is re-enabled. */
  sampleCount?: number;
}

export function RegistrationForm({
  header, patch, lookups, onNewLookup, onImport, onWarn, flash, triedSave, readOnly = false,
  savedJob = false, statusNameMap = {}, testCount = 0, sampleCount = 0,
}: RegistrationFormProps) {
  const flashCls = (id: string) => (flash.has(id) ? 'sr-flash' : undefined);
  const matrixChosen = header.matrices.length > 0;
  const gate = matrixChosen ? undefined : 'Select a Sample Matrix first — the test list depends on it.';

  // NOTE: these are plain FUNCTIONS invoked inline ({label(...)}), deliberately
  // not inner components.  A component defined inside a render body gets a new
  // type on every render, so React would remount every field and the input under
  // the cursor would lose focus after each keystroke.

  /** A label whose tooltip is the manual's Entry Guidance for that field. */
  const label = (text: string, required?: boolean) => (
    <label className={required ? 'req' : undefined} title={GUIDANCE[text]}>{text}</label>
  );

  /** A lookup field: label + grid-picker combo (+ inline ＋ when addable). */
  const lookupField = (
    text: string, field: keyof SrHeader, kind: SrLookupKey,
    opts: { required?: boolean; blocked?: string; id?: string; autoFocus?: boolean; white?: boolean; noNew?: boolean; rowValue?: (r: string[]) => string; selectedKey?: string; rowKey?: (r: string[]) => string } = {},
  ) => (
    <>
      {label(text, opts.required)}
      <LookupCombo
        id={opts.id}
        lookup={lookups[kind]}
        value={String(header[field] ?? '')}
        selectedKey={opts.selectedKey}
        rowKey={opts.rowKey}
        disabled={readOnly}
        blockedReason={opts.blocked}
        onBlocked={onWarn}
        onPick={row => applyPick(field, kind, row)}
        // `noNew` suppresses the inline ＋ for a SPECIFIC field even when its
        // lookup kind is NEWABLE — legacy (G5) shows these as plain dropdowns
        // (AllowNew="False"). Per-field so a shared kind (contact = Client
        // Contact + Invoice Contact; cocid = COC ID + COC Source) keeps ＋ on the
        // other field. (user request 2026-09-26)
        onNew={NEWABLE[kind] && !readOnly && !opts.noNew ? () => onNewLookup(kind) : undefined}
        newTitle={NEWABLE[kind] && !opts.noNew ? `New ${NEWABLE[kind]}` : undefined}
        // white = a non-fill-in combo that should show a white (not cream)
        // background (user request 2026-09-24).
        className={[flashCls(opts.id ?? String(field)), opts.white ? 'sr-combo-white' : '']
          .filter(Boolean).join(' ') || undefined}
        autoFocus={opts.autoFocus}
        rowValue={opts.rowValue}
      />
    </>
  );

  /** A plain input field.  `emptyText` renders a literal value when the field is
   *  empty (e.g. "Autogenerate" for Job ID is a real field VALUE, not grey
   *  placeholder text). */
  const inputField = (
    text: string, field: keyof SrHeader,
    opts: {
      type?: string; required?: boolean; ro?: boolean; placeholder?: string;
      maxWidth?: string; emptyText?: string; id?: string; min?: string;
    } = {},
  ) => {
    const val = String(header[field] ?? '');
    const ro = opts.ro || readOnly;
    const set = (v: string) => patch({ [field]: v } as Partial<SrHeader>);
    const cls = [
      opts.required && triedSave && !val.trim() ? 'sr-req-empty' : '',
      (opts.type === 'date' || opts.type === 'time' || opts.type === 'datetime-local') && !val ? 'sr-dt-empty' : '',
    ].filter(Boolean).join(' ') || undefined;
    // Date / time / datetime render through the shared 24-HOUR controls (GoLive
    // parity, user request 2026-09-24) — never AM/PM, whatever the browser locale.
    let control;
    if (opts.type === 'datetime-local') {
      control = <UsDateTimeInput id={opts.id} className={cls} mode="datetime" value={val} readOnly={ro} onChange={set} />;
    } else if (opts.type === 'time') {
      control = <UsDateTimeInput id={opts.id} className={cls} mode="time" value={val} readOnly={ro} onChange={set} />;
    } else if (opts.type === 'date') {
      control = <UsDateInput id={opts.id} className={cls} value={val} readOnly={ro} min={opts.min} onChange={set} />;
    } else {
      control = (
        <input
          id={opts.id}
          type={opts.type ?? 'text'}
          value={val || (opts.emptyText ?? '')}
          readOnly={ro}
          placeholder={opts.placeholder}
          style={opts.maxWidth ? { maxWidth: opts.maxWidth } : undefined}
          className={cls}
          onChange={e => set(e.target.value)}
        />
      );
    }
    return <>{label(text, opts.required)}{control}</>;
  };

  // Date / Time Collected are text boxes that keep what the user typed. A REJECTED value
  // (later than Received) must be CLEARED like legacy (ALPACA sets the property to
  // null/empty after the message) — the header value may already be '' so nothing
  // upstream changes; bumping the key remounts the box empty.
  const [dcKey, setDcKey] = useState(0);
  const [tcKey, setTcKey] = useState(0);

  /** Label + an arbitrary control. */
  const row = (text: string, control: ReactNode, required?: boolean) => (
    <>
      {label(text, required)}
      {control}
    </>
  );

  /** Selecting a lookup row also fills that lookup's dependent fields. */
  function applyPick(field: keyof SrHeader, kind: SrLookupKey, row: string[]) {
    // Permit's grid is ID·PermitType·Name·Number but the STORED value is the Name
    // (col index 2), matching Save/Get + oidByLabel (user request 2026-09-24).
    const value = kind === 'permit' ? (row[2] ?? '') : (row[0] ?? '');
    const next: Partial<SrHeader> = { [field]: value } as Partial<SrHeader>;
    if (kind === 'client' && field === 'client') {
      next.clientAddress = row[1] ?? '';
      next.clientPhone = row[2] ?? '';
      // row[3] is the hidden client Oid (mapLookupRows appends it). Capture the
      // EXACT client so Project / Collector / Contact / Quote scope correctly
      // even when two customers share a name. N/A pick (empty row) clears it.
      next.clientOid = row[3] ?? '';
      // CLIENT IS THE MASTER FIELD — everything scoped to it is cleared, because
      // those lists are re-fetched for the new client and any held value now
      // belongs to a DIFFERENT customer (legacy "ClientName" handler clears
      // ContactName, ProjectID and Collector; the read-only project fields go
      // with ProjectID).  DELIBERATE ADDITION: legacy leaves QuoteID pointing at
      // the old client's quote — a cross-client quote would drive the wrong
      // invoice, so it is cleared here too (§18.7 — don't carry a legacy defect).
      next.clientContact = '';
      // Account Number is client-scoped — clear it so a new client doesn't keep
      // the previous client's account (legacy re-scopes AccountNumberDataSource).
      next.accountNumber = '';
      next.invoiceContact = '';
      next.projectId = '';
      next.projectName = '';
      next.projectLocation = '';
      next.collector = [];
      next.quoteId = '';
    }
    if (kind === 'project' && field === 'projectId') {
      next.projectName = row[1] ?? '';
      next.projectLocation = row[2] ?? '';
      // Capture the exact Project Oid at pick time (mirrors clientOid above) so
      // "Import Pre-Log" can scope its picker immediately, even before Save —
      // the server only returns projectOid once the job is actually persisted.
      // Project IDs repeat across sites, so the label map would always return the
      // FIRST one — use the picked row's own Oid (hidden trailing element).
      next.projectOid = row[3] ?? lookups.project?.oidByLabel?.[value] ?? '';
    }

    // XAF ObjectSpace_ObjectChanged "QuoteID": a quote that carries a Project
    // defaults the job's Project ID (and, via the ProjectID handler, its Name +
    // Location). Quote rows are QuoteID · Title · ProjectID · ProjectName.
    if (kind === 'quote' && field === 'quoteId') {
      const quoteProject = row[2] ?? '';
      if (quoteProject) {
        const projRow = lookups.project?.rows?.find(r => r[0] === quoteProject);
        next.projectId = quoteProject;
        next.projectName = projRow?.[1] ?? row[3] ?? '';
        next.projectLocation = projRow?.[2] ?? '';
        next.projectOid = projRow?.[3] ?? lookups.project?.oidByLabel?.[quoteProject] ?? '';
      }
    }

    // ── XAF ObjectSpace_ObjectChanged: PaymentStatus → JobInactivation cascade ──
    // Legacy: COD auto-sets JobInactivation to "Hold"; anything else clears it.
    if (field === 'paymentStatus') {
      const action = value.toUpperCase() === 'COD' ? 'Hold' : '';
      next.jobInactivation = action;
      // Cascade into the inactivation state machine (same as picking it directly)
      applyInactivation(next, action);
    }

    // ── XAF ObjectSpace_ObjectChanged: JobInactivation → Status state machine ───
    if (field === 'jobInactivation') {
      applyInactivation(next, value);
    }

    patch(next);
  }

  /** Apply the inactivation state machine (Hold/Cancel/clear → status + cancelJob).
   *  Mutates the `next` partial in-place so the caller can batch-patch. */
  /** Resolve a status ID (e.g. "SR-2-8") to its display name via the rule-data
   *  map; falls back to the raw ID if the map hasn't loaded yet. */
  const statusName = (id: string) => statusNameMap[id] || id;

  function applyInactivation(next: Partial<SrHeader>, action: string) {
    if (action === 'Hold') {
      const isCod = (next.paymentStatus ?? header.paymentStatus)?.toUpperCase() === 'COD';
      const sid = isCod ? 'SR-2-8' : 'SR-2-7';
      next.status = statusName(sid);
      next.prevStatusId = header.status;
      next.cancelJob = false;
    } else if (action === 'Cancel') {
      if (header.receivedDate) {
        onWarn('Cannot cancel a job that has already been received.');
        next.jobInactivation = header.jobInactivation;
        return;
      }
      next.status = statusName('SR-2-6');
      next.prevStatusId = header.status;
      next.cancelJob = true;
    } else {
      next.status = header.prevStatusId || header.status;
      next.cancelJob = false;
    }
  }

  // Test(s) and No Of Samples are frozen once the job HAS samples; after Reset Job ID
  // (0 samples) they are editable again so the samples can be regenerated.
  const samplesLocked = savedJob && sampleCount > 0;

  const stepNo = (delta: number) => {
    if (!matrixChosen) { onWarn('Select a Sample Matrix first.'); return; }
    const n = Math.max(1, (Number.parseInt(header.noOfSamples, 10) || 0) + delta);
    patch({ noOfSamples: String(n) });
  };

  // ── Registration-type field gating (ALPACA parity) ────────────────────────
  // Ported from the legacy BO `Samplecheckin.cs` [Appearance] rules
  // "DropOffSamplesView" / "SamplingSamplesView".  Verified authoritative:
  // Model.xafml declares NO AppearanceRules, so nothing overrides them.
  //
  //   Regular       (IsSampling = false) → hide DateExpect, ProjectManager
  //   Pre-scheduled (IsSampling = true)  → hide RelinquishedBy, DateCollected,
  //                                        Collector, ManifestNumber,
  //                                        ReceivedDate, ReceivedBy, TimeCollected
  //
  // Legacy is `RegistrationType => IsSampling ? "Pre-scheduled" : "Regular"` —
  // a COMPUTED, read-only string, which is why the field stays `ro` above.
  // The API returns "Prescheduled" while the New popup sets "Pre-scheduled", so
  // the test tolerates BOTH spellings — a strict === would silently gate nothing.
  //
  // DEVIATION (owner decision 2026-07-27): legacy's SamplingSamplesView listed
  // "…RecievedDate;RecievedBy,TimeCollected" — a COMMA where XAF splits on ';',
  // so those last two were never actually hidden.  Treated as a typo and fixed
  // here per the rule's evident intent (§18.7: don't replicate legacy defects).
  //
  const isPrescheduled = /^pre-?scheduled$/i.test((header.registrationType ?? '').trim());
  const PRESCHEDULED_ONLY = ['dateExpect', 'projectManager', 'cocId', 'cocSource'];
  // Date Expect min = TODAY (user request 2026-09-26): today and future are
  // selectable, only PAST dates are disabled (earlier "strictly future" was
  // changed to include today — "26 show pannanum today + future").
  const dateExpectMin = (() => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();
  const REGULAR_ONLY = [
    'relinquishedBy', 'dateCollected', 'collector',
    'manifestNumber', 'receivedDate', 'receivedBy', 'timeCollected',
  ];
  /** Legacy [Appearance] visibility for one field, by the current reg type. */
  const showField = (f: string): boolean =>
    PRESCHEDULED_ONLY.includes(f) ? isPrescheduled
      : REGULAR_ONLY.includes(f) ? !isPrescheduled
        : true;

  // Quote ID (with its Import button) appears in both the Regular and
  // Pre-scheduled layout branches below — define it once so they can't drift.
  const quoteIdField = row('Quote ID', (
    <div className="sr-fldrow">
      <LookupCombo
        lookup={lookups.quote}
        value={header.quoteId}
        disabled={readOnly}
        onPick={r => applyPick('quoteId', 'quote', r)}
        className="sr-combo-white"
      />
      <button className="sr-fldbtn" onClick={() => onImport('quote')} title="Import Samples">
        ⬇ Import
      </button>
    </div>
  ));

  return (
    <>
      {/* ── General Information ───────────────────────────────────────────── */}
      <section className="sr-sec">
        <h3 className="sr-sec-h"><span className="sr-sec-ico">📋</span> General Information</h3>
        <div className="sr-sec-b">
          <div className="sr-fgrid">
            {/* Prototype shows the literal word "Autogenerate" IN the read-only
                field until the id is minted — not a grey placeholder. */}
            {inputField('Job ID', 'jobId', { ro: true, emptyText: 'Autogenerate' })}
            {showField('relinquishedBy') && row('Relinquished By', (
              <LookupCombo
                lookup={lookups.relinq}
                multi
                values={header.relinquishedBy}
                disabled={readOnly}
                onToggle={next => patch({ relinquishedBy: next })}
                onNew={readOnly ? undefined : () => onNewLookup('relinq')}
                newTitle="New Relinquished By"
                className="sr-combo-white"
              />
            ))}

            {showField('receivedDate') && inputField('Received Date', 'receivedDate', { type: 'datetime-local' })}
            {showField('collector') && row('Collector', (
              <LookupCombo
                lookup={lookups.collector}
                multi
                values={header.collector}
                disabled={readOnly}
                onToggle={next => patch({ collector: next })}
                onNew={readOnly ? undefined : () => onNewLookup('collector')}
                newTitle="New Collector"
                className="sr-combo-white"
              />
            ))}

            {/* Received By reads the GLOBAL employee lookup (AI_Employee_Lookup_SP)
                supplied through `lookups.employee` — not a form-local list. */}
            {showField('receivedBy') && lookupField('Received By', 'receivedBy', 'employee', { id: 'sr-f-employee', white: true })}
            {showField('dateCollected') && row('Date Collected', (
              <UsDateInput
                key={dcKey}
                value={header.dateCollected}
                className={!header.dateCollected ? 'sr-dt-empty' : undefined}
                readOnly={readOnly}
                onChange={v => {
                  // Legacy (SampleRegistrationViewController DateCollected handler): a
                  // collected date after the received date shows the message and CLEARS
                  // Date Collected.
                  if (v && header.receivedDate && v > header.receivedDate) {
                    onWarn('The collected date must be less than the received date.');
                    patch({ dateCollected: '' });
                    setDcKey(k => k + 1);
                    return;
                  }
                  // Same day, but the already-entered Time Collected is later than the
                  // received time → message and clear the TIME (legacy second branch).
                  if (v && header.timeCollected && header.receivedDate
                    && v === header.receivedDate.slice(0, 10)) {
                    const recTime = header.receivedDate.length > 10
                      ? header.receivedDate.slice(11, 16) : '23:59';
                    if (header.timeCollected > recTime) {
                      onWarn('The collected time must be less than the received date time.');
                      patch({ dateCollected: v, timeCollected: '' });
                      setTcKey(k => k + 1);
                      return;
                    }
                  }
                  patch({ dateCollected: v });
                }}
              />
            ))}

            {/* Created By — a read-only DROPDOWN-STYLE control (G5 parity: legacy
               renders CreatedBy as a lookup editor with AllowEdit="False"). Native
               <select disabled> gives the dropdown chevron; it shows the current
               user and cannot be changed. */}
            {row('Created By', (
              <select
                className="sr-ro-combo"
                aria-label="Created By"
                value={String(header.createdBy ?? '')}
                disabled
              >
                <option value={String(header.createdBy ?? '')}>
                  {String(header.createdBy ?? '')}
                </option>
              </select>
            ))}
            {showField('timeCollected') && row('Time Collected', (
              <UsDateTimeInput
                key={tcKey}
                mode="time"
                value={header.timeCollected}
                className={!header.timeCollected ? 'sr-dt-empty' : undefined}
                readOnly={readOnly}
                onChange={v => {
                  if (v && !v.includes(':')) {
                    onWarn('Time collected format is not correct.');
                    patch({ timeCollected: '' });
                    setTcKey(k => k + 1);
                    return;
                  }
                  if (v && header.dateCollected && header.receivedDate
                    && header.dateCollected === header.receivedDate.slice(0, 10)) {
                    const recTime = header.receivedDate.length > 10
                      ? header.receivedDate.slice(11, 16) : '23:59';
                    if (v > recTime) {
                      // Legacy TimeCollected handler: message, then the time is cleared.
                      onWarn('The collected time must be less than the received date time.');
                      patch({ timeCollected: '' });
                      setTcKey(k => k + 1);
                      return;
                    }
                  }
                  patch({ timeCollected: v });
                }}
              />
            ))}

            {/* selectedKey/rowKey tie the open dropdown's highlight to the EXACT
                picked customer (its Oid, carried as the hidden row[3]) so a
                different customer that happens to share the name isn't shown as
                selected (user report 2026-10-03). */}
            {lookupField('Client', 'client', 'client', { required: true, id: 'sr-f-client', autoFocus: true,
              selectedKey: header.clientOid, rowKey: r => r[3] ?? '' })}
            {/* Project ID is mandatory ONLY for a Pre-scheduled job — ALPACA shows
                the red * for Pre-scheduled and NOT for Regular (user report
                2026-10-05). The third `row(...)` arg (required) is therefore
                `isPrescheduled`, not a hardcoded `true`. This matches the save
                guard (missingRequired pushes Project ID only when isPrescheduled)
                and the empty-field tint (sr-req-empty, also isPrescheduled-only). */}
            {row('Project ID', (
              <div className="sr-fldrow">
                <LookupCombo
                  id="sr-f-project"
                  lookup={lookups.project}
                  value={header.projectId}
                  disabled={readOnly}
                  onPick={r => applyPick('projectId', 'project', r)}
                  onNew={readOnly ? undefined : () => onNewLookup('project')}
                  newTitle="New Project"
                  className={triedSave && isPrescheduled && !header.projectId ? 'sr-req-empty' : undefined}
                />
                <button
                  className="sr-fldbtn"
                  // "Import Pre-Log" is a ONE-SHOT create action — it mints the Job ID
                  // AND populates the job from the COC pre-log in the same click. Once
                  // the job has a real, saved Job ID (savedJob), that door is closed —
                  // this button is for a brand-new draft only, never a follow-up
                  // action on an already-created job (business decision 2026-09-01;
                  // supersedes the "SampleTestCount=0 only" reading of legacy's gate).
                  // G5 parity (user request): the button is ENABLED BY DEFAULT on a new
                  // draft — the Client / Project / Date Expect prerequisites are NOT a
                  // disable-gate but are validated on click by handleImport('coc')
                  // ("Fill these before importing a Pre-Log: …"), exactly like G5's
                  // always-clickable orange import button. Only the one-shot-create rule
                  // (readOnly · savedJob · tests already assigned) still greys it out.
                  disabled={readOnly || (savedJob && sampleCount > 0) || testCount > 0}
                  onClick={() => onImport('coc')}
                  title={
                    savedJob && sampleCount > 0 ? 'Import COC is only available while the job has no samples — use Reset Job ID to clear them first.'
                    : !header.client || !header.projectId ? 'Select a Client and Project first.'
                    : (isPrescheduled && !header.dateExpect) ? 'Date Expect must not be empty.'
                    : testCount > 0 ? 'Tests are already assigned — Import COC is only available before any test is added.'
                    : 'Import COC — saves the job and imports in one step.'
                  }
                >
                  ⬇ Import
                </button>
              </div>
            ), isPrescheduled)}

            {inputField('Client Address', 'clientAddress', { ro: true })}
            {inputField('Project Name', 'projectName', { ro: true })}

            {inputField('Client Phone', 'clientPhone', { ro: true })}
            {inputField('Project Location', 'projectLocation', { ro: true })}

            {lookupField('Client Contact', 'clientContact', 'contact')}
            {lookupField('Permit', 'permit', 'permit', { rowValue: r => r[2] ?? '', noNew: true, white: true })}

            {/* Account Number — below Client Contact (G5 parity: Samplecheckin
               SelectedAccount, the selected client's non-suspended accounts). The
               right pair is a spacer so it sits directly under Client Contact. */}
            {lookupField('Account Number', 'accountNumber', 'account', { noNew: true, white: true })}

            {/* G5 parity (user report): the right column flows continuously — NO
                empty cell after Permit. Regular and Pre-scheduled hold different
                field sets (Manifest Number is Regular-only; COC ID / Date Expect /
                Project Manager are Pre-scheduled-only), so each mode interleaves
                its own fields left↔right and neither shows a gap.
                COC_ID is the read-only strCOCID ('COC' + COC_ID, e.g. "COC0321"),
                bound to cocSource and resolved in AI_SampleRegistration_Get_SP. */}
            {isPrescheduled ? (
              <>
                {/* Pre-scheduled: NO empty cell between Permit and Project Category
                    (user report 2026-10-05 — the old sr-fgrid-gap left a blank right
                    cell next to Account Number). The right column now flows
                    continuously Permit → Project Category → Invoice Contact →
                    Registration Type → COC ID → Date Expect, with Project Category
                    pairing with Account Number (emitted just above). Each left field
                    is immediately followed by its right partner so the two columns
                    stay aligned with no gap. */}
                {lookupField('Project Category', 'projectCategory', 'category', { white: true })}
                {quoteIdField}
                {lookupField('Invoice Contact', 'invoiceContact', 'invoicecontact', { white: true, noNew: true })}
                {inputField('PO #', 'poNumber')}
                {inputField('Registration Type', 'registrationType', { ro: true })}
                {lookupField('Payment Status', 'paymentStatus', 'payment', { white: true, noNew: true })}
                {row('COC ID', (
                  <select className="sr-ro-combo" aria-label="COC ID"
                    value={header.cocSource || ''} disabled>
                    <option value={header.cocSource || ''}>{header.cocSource || 'N/A'}</option>
                  </select>
                ))}
                {lookupField('Job Inactivation', 'jobInactivation', 'inact', { white: true })}
                {inputField('Date Expect', 'dateExpect', { type: 'date', required: true, id: 'sr-f-date-expect', min: dateExpectMin })}
                {inputField('Status', 'status', { ro: true })}
                {lookupField('Project Manager', 'projectManager', 'pm', { noNew: true })}
              </>
            ) : (
              <>
                {lookupField('Project Category', 'projectCategory', 'category', { white: true })}
                {quoteIdField}
                {lookupField('Invoice Contact', 'invoiceContact', 'invoicecontact', { white: true, noNew: true })}
                {inputField('PO #', 'poNumber')}
                {inputField('Registration Type', 'registrationType', { ro: true })}
                {lookupField('Payment Status', 'paymentStatus', 'payment', { white: true, noNew: true })}
                {lookupField('Job Inactivation', 'jobInactivation', 'inact', { white: true })}
                {showField('manifestNumber') && inputField('Manifest Number', 'manifestNumber', { type: 'number' })}
                {inputField('Status', 'status', { ro: true })}
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── Sample Information ────────────────────────────────────────────── */}
      <section className="sr-sec">
        <h3 className="sr-sec-h"><span className="sr-sec-ico">🧪</span> Sample Information</h3>
        <div className="sr-sec-b">
          <div className="sr-fgrid">
            {row('Sample Matrix', (
              <LookupCombo
                id="sr-f-matrix"
                lookup={lookups.matrix}
                multi
                values={header.matrices}
                disabled={readOnly}
                placeholder="Select…"
                onToggle={next => {
                  if (savedJob && header.matrices.some(v => !next.includes(v))) {
                    onWarn('Sample Matrix cannot be removed after a Job ID has been created. You may add additional matrices.');
                    return;
                  }
                  patch({ matrices: next });
                }}
                className={flashCls('sr-f-matrix')}
              />
            ), true)}
            {row('Sample Category', (
              <LookupCombo
                lookup={lookups.sampleCategory}
                multi
                values={header.sampleCategory}
                disabled={readOnly}
                onToggle={next => patch({ sampleCategory: next })}
                className="sr-combo-white"
              />
            ))}

            {row('Test', (
              /* Header-level test template — each generated sample starts from
                 this set and is customised per sample in Test Assignment. */
              <div className={matrixChosen ? undefined : 'sr-gated'}>
                <LookupCombo
                  id="sr-f-test"
                  lookup={lookups.test}
                  multi
                  gridStyle
                  values={header.testTemplate}
                  disabled={readOnly}
                  readOnlyItems={samplesLocked}
                  placeholder="Select…"
                  blockedReason={gate}
                  onBlocked={onWarn}
                  // XAF parity: the stored value + closed-field label is the
                  // composite "TestName|MethodNumber" (row[0]|row[1]) so two
                  // same-named tests are distinct and each resolves to its own
                  // TestMethod Oid. The grid still shows Test / Method columns.
                  rowValue={(r) => (r[1] ? `${r[0]}|${r[1]}` : (r[0] ?? ''))}
                  onToggle={(next, picked) => {
                    const composite = picked[0]
                      ? (picked[1] ? `${picked[0]}|${picked[1]}` : picked[0])
                      : '';
                    patch({
                      testTemplate: next,
                      testTemplateMethods: composite
                        ? { ...header.testTemplateMethods, [composite]: picked[1] ?? '' }
                        : header.testTemplateMethods,
                    });
                  }}
                />
              </div>
            ))}
            {inputField('Cooler ID', 'coolerId')}

            {row('No Of Samples', (
              <span className="sr-numspin sr-numspin-fill">
                <button type="button" onClick={() => stepNo(-1)} disabled={samplesLocked} aria-label="Fewer samples">−</button>
                <input value={header.noOfSamples} type="number" min={1}
                  placeholder="—" aria-label="No Of Samples"
                  disabled={samplesLocked}
                  onChange={samplesLocked ? undefined : e => patch({ noOfSamples: e.target.value })}
                  onBlur={samplesLocked ? undefined : () => {
                    const n = Number.parseInt(header.noOfSamples, 10);
                    if (!header.noOfSamples || isNaN(n) || n < 1)
                      patch({ noOfSamples: '1' });
                  }}
                />
                <button type="button" onClick={() => stepNo(1)} disabled={samplesLocked} aria-label="More samples">＋</button>
              </span>
            ))}
            {row('Remark', (
              <textarea rows={2} value={header.remark}
                readOnly={readOnly}
                onChange={e => patch({ remark: e.target.value })} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Report Requirements ───────────────────────────────────────────── */}
      <section className="sr-sec">
        <h3 className="sr-sec-h"><span className="sr-sec-ico">📑</span> Report Requirements</h3>
        <div className="sr-sec-b">
          <div className="sr-fgrid">
            {row('TAT', (
              <LookupCombo
                lookup={lookups.tat}
                value={header.tat}
                disabled={readOnly}
                onPick={r => patch({ tat: r[0] ?? '' })}
              />
            ))}
            {lookupField('EDD Export Format', 'eddFormat', 'edd', { white: true })}

            {row('Due Date', (
              <input type="datetime-local"
                value={header.dueDate ? header.dueDate.slice(0, 16) : ''}
                className={!header.dueDate ? 'sr-dt-empty' : undefined}
                onChange={e => patch({ dueDate: e.target.value })} />
            ))}
            {inputField('Report Delivery Address', 'reportDeliveryAddress')}

            {lookupField('Report Template', 'reportTemplate', 'rpt', { white: true })}
            {inputField('Report Delivery Method', 'reportDeliveryMethod')}

            {lookupField('EDD Template', 'eddTemplate', 'eddtpl', { white: true })}
            <span />
          </div>
        </div>
      </section>
    </>
  );
}
